import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../lib/supabaseClient';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { user, userRole, loading, refreshRole } = useSupabaseAuth();
  const [reconciling, setReconciling] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const hasHandled = useRef(false);

  useEffect(() => {
    if (loading || reconciling || ensuring || hasHandled.current) return;

    if (user && userRole) {
      // User has a role — proceed normally
      hasHandled.current = true;
      handleAuthComplete(user, userRole);
    } else if (user && !userRole) {
      // User exists but no role — new OAuth signup
      // The DB trigger should have created the role, but call fallback RPC just in case
      hasHandled.current = true;
      ensureCandidateRole(user);
    } else if (!user) {
      // No user at all — OAuth failed
      hasHandled.current = true;
      navigate('/login?error=oauth_failed', { replace: true });
    }
  }, [user, userRole, loading, reconciling, ensuring]);

  const ensureCandidateRole = async (authUser) => {
    setEnsuring(true);
    try {
      console.log('Auth callback: No role found for', authUser.email, '— creating candidate role');

      const { data, error } = await supabase.rpc('ensure_candidate_role');

      if (error) {
        console.error('Error ensuring candidate role:', error);
        navigate('/login?error=role_creation_failed', { replace: true });
        return;
      }

      console.log('ensure_candidate_role result:', data);

      // Refresh the role in the auth context
      if (refreshRole) {
        await refreshRole(authUser.id);
      }

      // Proceed with candidate flow
      handleAuthComplete(authUser, data?.role || 'candidate');
    } catch (error) {
      console.error('Error in ensureCandidateRole:', error);
      navigate('/login?error=role_creation_failed', { replace: true });
    } finally {
      setEnsuring(false);
    }
  };

  const handleAuthComplete = async (authUser, role) => {
    setReconciling(true);

    try {
      console.log('Auth callback: Processing auth completion for', authUser.email);

      // If user is a candidate, reconcile pending applications and link candidate record
      if (role === 'candidate') {
        console.log('Running candidate reconciliation...');

        // First, ensure candidate record has user_id linked
        const { data: candidate, error: candidateError } = await supabase
          .from('candidates')
          .select('id, user_id')
          .eq('email', authUser.email)
          .maybeSingle();

        if (candidate && !candidate.user_id) {
          console.log('Linking candidate to user_id:', authUser.id);
          const { error: updateError } = await supabase
            .from('candidates')
            .update({ user_id: authUser.id })
            .eq('id', candidate.id);

          if (updateError) {
            console.error('Error linking candidate to user:', updateError);
          } else {
            console.log('Candidate linked successfully');
          }
        }

        // Run reconciliation for pending applications
        const { data: reconcileResult, error: reconcileError } = await supabase.rpc(
          'reconcile_pending_applications',
          {
            p_auth_user_id: authUser.id,
            p_email: authUser.email
          }
        );

        if (reconcileError) {
          console.error('Error reconciling applications:', reconcileError);
        } else if (reconcileResult && reconcileResult.reconciled_count > 0) {
          console.log(`Reconciled ${reconcileResult.reconciled_count} pending application(s)`);
        }
      }

      // For candidates, check if onboarding is complete
      if (role === 'candidate') {
        const { data: candidateData } = await supabase
          .from('candidates')
          .select('onboarding_complete')
          .eq('user_id', authUser.id)
          .maybeSingle();

        if (!candidateData || !candidateData.onboarding_complete) {
          navigate('/candidate/onboarding', { replace: true });
          return;
        }
      }

      // If the user came from the apply page (OAuth login), send them back there
      const returnUrl = sessionStorage.getItem('oauth_return_url');
      if (returnUrl) {
        sessionStorage.removeItem('oauth_return_url');
        navigate(returnUrl, { replace: true });
        return;
      }

      // Redirect based on user role
      redirectBasedOnRole(role);
    } catch (error) {
      console.error('Error during auth callback processing:', error);
      // Still redirect even if reconciliation fails
      redirectBasedOnRole(role);
    } finally {
      setReconciling(false);
    }
  };

  const redirectBasedOnRole = (role) => {
    switch (role) {
      case 'recruiter':
      case 'admin':
        navigate('/recruiter', { replace: true });
        break;
      case 'client':
        navigate('/client', { replace: true });
        break;
      case 'candidate':
        navigate('/candidate', { replace: true });
        break;
      default:
        navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900">Completing sign in...</h2>
        <p className="mt-2 text-sm text-gray-600">Please wait while we redirect you</p>
      </div>
    </div>
  );
};

export default AuthCallback;
